// Phase I4 — Daily Acca page rebuild.
//
// Public, anon-readable. Three layout directions (A / B / C) backed by
// the same useFreeTierToday hook. Tier-aware: free users see the 3-leg
// slip, Pro users see the 5-leg slip — both fetched from
// `free_tier_daily_acca` keyed on (acca_date, tier).
//
// Doctrine: never fabricate. When the row is missing for the user's
// tier, the direction renders the empty-state copy preserved from the
// Phase A page.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFreeTierToday } from "../../free-tier/useFreeTierToday.ts";
import type { FreeAcca } from "../../free-tier/useFreeTierToday.ts";
import { useUserTier } from "../../auth/useUserTier.ts";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { getEffectiveTier } from "../../free-tier/getEffectiveTier.ts";
import { useTodayShareCard } from "../../free-tier/useTodayShareCard.ts";
import { useConversionTriggerPump } from "../../free-tier/useConversionTriggerPump.ts";
import { DailyFixtureRail } from "../../components/super-acca";
import { SignalMenuPanel } from "../../components/super-acca-extras/SignalMenuPanel.tsx";
import { PerformanceHeader } from "../../components/super-acca-extras/PerformanceHeader.tsx";
import { IntelligenceSignalBar } from "../../components/super-acca-extras/IntelligenceSignalBar.tsx";
import { YesterdayResults } from "../../components/super-acca-extras/YesterdayResults.tsx";
import { NoBetReport } from "../../components/super-acca-extras/NoBetReport.tsx";
import { AnnouncementCard } from "../../components/super-acca-extras/AnnouncementCard.tsx";
import { GapReportCard } from "../../components/super-acca-extras/GapReportCard.tsx";
import { PhantomBetCard } from "../../components/super-acca-extras/PhantomBetCard.tsx";
import { CommunityLedger } from "../../components/super-acca-extras/CommunityLedger.tsx";
import { ReferralCard } from "../../components/super-acca-extras/ReferralCard.tsx";
import { TrialCard } from "../../components/super-acca-extras/TrialCard.tsx";
import { TriggerPrompt } from "../../components/super-acca-extras/TriggerPrompt.tsx";
import { WorldCupCard } from "../../components/super-acca-extras/WorldCupCard.tsx";
import { FounderOfferCard } from "../../components/super-acca-extras/FounderOfferCard.tsx";
import { SlipBuilder, useSlipDraft } from "../../components/super-acca-extras/SlipBuilder.tsx";
import { WinCelebration } from "../../components/super-acca-extras/WinCelebration.tsx";
import { addLeg, legFromSignal } from "../../components/super-acca-extras/slipBuilder.ts";
import { useDailyAccaIntel } from "../../free-tier/useDailyAccaIntel.ts";
import { useUserSlips, type UserSlip } from "../../free-tier/useUserSlips.ts";
import type { DailyAccaSignal } from "../../free-tier/useDailyAccaSignals.ts";
import { DailyAccaA } from "./directions/DailyAccaA.tsx";
import { DailyAccaB } from "./directions/DailyAccaB.tsx";
import { DailyAccaC } from "./directions/DailyAccaC.tsx";
import {
  DirectionToggle,
  TierToggle,
  UpgradeCta,
  type Direction,
} from "./directions/PageChrome.tsx";
import { DoctrineSection } from "../../components/DoctrineSection.tsx";
import { useDoctrinePicks } from "../../doctrine/useDoctrinePicks.ts";

const SHARE_CARD_ORIGIN = "https://strikza.app";
const DIRECTION_KEY = "strikza:dailyacca:direction";

function readDirection(): Direction {
  try {
    const v = localStorage.getItem(DIRECTION_KEY);
    if (v === "A" || v === "B" || v === "C") return v;
  } catch {
    /* SSR / blocked storage */
  }
  return "A";
}

function useDailyAccaOgMeta(acca: FreeAcca | null, todayCardId: string | null): void {
  useEffect(() => {
    const prevTitle = document.title;
    const ogImageUrl =
      todayCardId !== null
        ? `${SHARE_CARD_ORIGIN}/api/og/share-card?card_id=${todayCardId}`
        : `${SHARE_CARD_ORIGIN}/api/og/share-card`;
    const legCount = acca ? String(acca.leg_count) : "";
    const odds =
      acca && Number.isFinite(acca.combined_odds) ? `${acca.combined_odds.toFixed(2)}×` : "";
    const title = `STRIKZA · Daily Acca${legCount ? ` · ${legCount}-leg` : ""}${odds ? ` · ${odds}` : ""}`;
    const description = "Today's free acca + Founder's Pick. Free, every day.";
    const url = `${SHARE_CARD_ORIGIN}/daily-acca`;
    document.title = title;
    const tags: Array<{ key: string; attr: "property" | "name"; content: string }> = [
      { key: "og:title", attr: "property", content: title },
      { key: "og:description", attr: "property", content: description },
      { key: "og:image", attr: "property", content: ogImageUrl },
      { key: "og:url", attr: "property", content: url },
      { key: "og:type", attr: "property", content: "website" },
      { key: "twitter:card", attr: "name", content: "summary_large_image" },
      { key: "twitter:title", attr: "name", content: title },
      { key: "twitter:description", attr: "name", content: description },
      { key: "twitter:image", attr: "name", content: ogImageUrl },
    ];
    const created: HTMLMetaElement[] = [];
    for (const t of tags) {
      const sel = `meta[${t.attr}="${t.key}"]`;
      let el = document.head.querySelector<HTMLMetaElement>(sel);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(t.attr, t.key);
        document.head.appendChild(el);
        el.dataset["strikzaOg"] = "1";
        created.push(el);
      }
      el.setAttribute("content", t.content);
    }
    return () => {
      document.title = prevTitle;
      for (const el of created) el.parentNode?.removeChild(el);
    };
  }, [acca, todayCardId]);
}

export function DailyAccaPage(): JSX.Element {
  const { acca, founder, recentAccaResult, loading, error } = useFreeTierToday();
  const intel = useDailyAccaIntel();
  const { tierCode } = useUserTier();
  const effectiveTier = getEffectiveTier(tierCode);
  const isPro = effectiveTier === "pro";
  // Phase JB.x fix — anon-readable Daily Acca. `authenticated` must come
  // from a real auth signal, not the tier code (free auth users have
  // tierCode === null, which previously misclassified them as anon).
  const { user } = useAuthSession();
  const authenticated = user !== null;
  const { cardId: todayCardId } = useTodayShareCard();
  useDailyAccaOgMeta(acca, todayCardId);

  const [direction, setDirection] = useState<Direction>(() => readDirection());
  useEffect(() => {
    try {
      localStorage.setItem(DIRECTION_KEY, direction);
    } catch {
      /* ignore */
    }
  }, [direction]);

  useConversionTriggerPump(
    acca ? "daily_acca_view" : null,
    acca ? { acca_id: acca.id, acca_date: acca.acca_date } : {},
  );

  const {
    probabilityPicks: doctrineProbPicks,
    valuePicks: doctrineValuePicks,
    substrateEmpty,
    substrateWeek,
  } = useDoctrinePicks({ t1HasPicks: !!acca });

  const directionProps = { acca, founder, recentAccaResult, isPro, loading };

  // Phase JA.4 — slip builder draft (anon-friendly localStorage) +
  // user-slips realtime (auth only).
  const navigate = useNavigate();
  const { draft, setDraft } = useSlipDraft();
  const { slips: userSlips } = useUserSlips();
  const [celebratedSlipIds, setCelebratedSlipIds] = useState<Set<string>>(() => new Set());
  const [pendingCelebration, setPendingCelebration] = useState<UserSlip | null>(null);

  const draftSignalIds = useMemo(
    () => new Set(draft.legs.map((l) => l.signal_id)),
    [draft.legs],
  );

  const onAddToSlip = (signal: DailyAccaSignal): void => {
    setDraft((d) => addLeg(d, legFromSignal(signal)));
  };

  const latestLockedSlip = userSlips.length > 0 ? userSlips[0] ?? null : null;

  // Detect a transition to outcome='won' — only fire celebration on
  // a real DB row, never on a fabricated client state.
  useEffect(() => {
    const won = userSlips.find(
      (s) => s.outcome === "won" && !celebratedSlipIds.has(s.slip_id),
    );
    if (won) {
      setPendingCelebration(won);
      setCelebratedSlipIds((prev) => {
        const next = new Set(prev);
        next.add(won.slip_id);
        return next;
      });
    }
  }, [userSlips, celebratedSlipIds]);

  const onAuthRequired = (): void => {
    navigate("/auth");
  };

  return (
    <div
      className="sa-root"
      data-screen-label="00 DAILY ACCA"
      style={{ background: "var(--sa-bg-base)", color: "var(--sa-fg-primary)", minHeight: "var(--vh-full)" }}
    >
      <header
        style={{
          padding: "20px 32px",
          borderBottom: "1px solid var(--sa-hairline)",
          display: "flex",
          alignItems: "center",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <Link
          to="/"
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 13,
            letterSpacing: "0.22em",
            color: "var(--sa-fg-primary)",
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          STRIKZA
        </Link>
        <span style={{ flex: 1 }} />
        <DirectionToggle value={direction} onChange={setDirection} />
        <TierToggle effectiveTier={effectiveTier} authenticated={authenticated} />
      </header>

      <DailyFixtureRail />

      {/* JB.3c — World Cup card sits at the top (anon + auth). */}
      <WorldCupCard />

      <PhantomBetCard />

      <PerformanceHeader
        performance={intel.performance}
        rollingWinRate7d={intel.rollingWinRate7d}
        streak={intel.streak}
        loading={intel.loading}
      />

      <TrialCard source="manual" />
      {/* JB.3c — Founder offer card. Auth + free + eligible only. */}
      <FounderOfferCard />
      <TriggerPrompt />

      {direction === "A" ? (
        <DailyAccaA {...directionProps} />
      ) : direction === "B" ? (
        <DailyAccaB {...directionProps} />
      ) : (
        <DailyAccaC {...directionProps} />
      )}

      {!acca && !loading && (
        <div style={{ padding: "0 48px", maxWidth: 1180, margin: "0 auto" }}>
          <DoctrineSection
            probabilityPicks={doctrineProbPicks}
            valuePicks={doctrineValuePicks}
            isFullFallback
            substrateEmpty={substrateEmpty}
            substrateWeek={substrateWeek}
          />
        </div>
      )}

      <IntelligenceSignalBar counts={intel.todayCounts} loading={intel.loading} />

      <section
        aria-label="Daily Acca Slip Builder"
        style={{ padding: "0 48px 0", maxWidth: 1180, margin: "0 auto" }}
      >
        <SlipBuilder
          draft={draft}
          setDraft={setDraft}
          onAuthRequired={onAuthRequired}
          latestLockedSlip={latestLockedSlip}
        />
      </section>

      <section
        aria-label="Daily Acca Signal Menu"
        style={{ padding: "16px 48px 24px", maxWidth: 1180, margin: "0 auto" }}
      >
        <SignalMenuPanel
          showAnonUpsell={!isPro}
          onAddToSlip={onAddToSlip}
          draftSignalIds={draftSignalIds}
        />
      </section>

      <YesterdayResults rows={intel.yesterdayResults} loading={intel.loading} />

      <NoBetReport
        breakdown={intel.suppressionBreakdown}
        totalSuppressed={intel.todayCounts.total_suppressed}
        loading={intel.loading}
      />

      <CommunityLedger />

      <AnnouncementCard />

      <ReferralCard />

      <GapReportCard />

      {error !== null ? (
        <div
          style={{
            margin: "0 32px 32px",
            padding: 18,
            border: "1px solid var(--sa-signal-red)",
            background: "var(--sa-signal-red-dim)",
            borderRadius: 6,
            fontFamily: "var(--sa-font-body)",
            color: "var(--sa-fg-primary)",
          }}
        >
          <strong>Could not load free surfaces.</strong> {error}
        </div>
      ) : null}

      <UpgradeCta />

      <WinCelebration
        slip={pendingCelebration}
        onDismiss={() => setPendingCelebration(null)}
      />

      <footer
        style={{
          padding: "20px 32px 40px",
          fontFamily: "var(--sa-font-body)",
          fontSize: 12,
          color: "var(--sa-fg-muted)",
          textAlign: "center",
        }}
      >
        STRIKZA never places bets. You copy any slip to your bookmaker manually. Phase 1A · simulated mode.
      </footer>
    </div>
  );
}
